import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Route, Router, Routes } from '@angular/router';
import { select, Store } from '@ngrx/store';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { Observable, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { AumicoUser } from '../../../interfaces/aumico.routing';
import { animateText, onSideNavChange } from '../../animations/animations';
import { AumicoCoreLayoutProvider } from '../../ngrx/aumico.layout.provider';
import * as fromLayout from '../../ngrx/aumico.layout.reducer';
import {
    ModuleState,
    selectPrimaryNavigation,
    selectSecondaryNavigation,
    selectUser,
    selectUserNavigation,
} from '../../ngrx/aumico.layout.reducer';
import { SidenavService } from '../../services/sidenav.service';

const debug = Debug('modeso:dgoods-admin:SidebarComponent');
const NOT_FOUND_INDEX = -1;

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    animations: [onSideNavChange, animateText],
})
export class SidebarComponent implements OnInit, OnDestroy {
    private subscriptions = new Array<Subscription>();

    primaryNavigation$: Observable<Routes>;
    secondaryNavigation$: Observable<Routes>;
    userNavigation$: Observable<Route | null>;
    activePage$: Observable<Route>;
    user$: Observable<AumicoUser>;

    // If initial true, it will be open on initial load
    public sideNavState = true;
    // If initial true, it will be open on initial load
    public linkText = true;
    public activeRoute = 'undefined';

    constructor(
        private router: Router,
        private sidenavService: SidenavService,
        private layoutStore: Store<ModuleState>,
        private aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
    ) {
        this.primaryNavigation$ = this.layoutStore.pipe(select(selectPrimaryNavigation));
        this.secondaryNavigation$ = this.layoutStore.pipe(select(selectSecondaryNavigation));
        this.userNavigation$ = this.layoutStore.pipe(select(selectUserNavigation));
        this.user$ = this.layoutStore.pipe(select(selectUser));
        this.activePage$ = this.layoutStore.pipe(select(fromLayout.selectActivePage));
    }

    ngOnInit(): void {
        debug('init');
        this.subscriptions.push(
            this.sidenavService.sideNavState$.subscribe((res) => {
                debug('sidenavService.sideNavState$', res);
                this.sideNavState = res;
                this.linkText = res;
            }),
        );
        this.secondaryNavigation$.pipe(
            tap((item) => {
                debug('new');
                debug(item);
            }),
        );
        this.initRouteObserver();
    }

    private initRouteObserver() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(this.onPageNavigationEnd());
    }

    onClick(page: Route): void {
        if (page.path == null) {
            throw new Error('There is no path for the clicked route');
        }
        const segments = page.path.split('/');
        // length -1 to get last segment
        const mainPathSegment = segments[segments.length - 1];
        // getting parent Index
        const currentRouteMainPageIndex = this.router.config.findIndex(
            // taking index 1 because index 0 is for the language
            (route) => route.path != null && route.path.split('/')[1] === mainPathSegment,
        );

        if (currentRouteMainPageIndex === NOT_FOUND_INDEX) {
            throw new Error('The clicked route is not exist in the router configuration');
        }

        const routeConfiguration = this.router.config[currentRouteMainPageIndex];
        const currentRouteMainPage = cloneDeep<Route>(routeConfiguration);

        if (currentRouteMainPage.path == null) {
            throw new Error('There is no path for the clicked route in router configuration');
        }

        currentRouteMainPage.path = currentRouteMainPage.path.replace(':language', segments[0]);

        this.aumicoCoreLayoutProvider.dispatchNavigateToRoute({ route: currentRouteMainPage });
    }

    private onPageNavigationEnd() {
        return (event) => {
            this.activeRoute = event.urlAfterRedirects;
            debug(event);
        };
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    }

    onSinenavToggle(): void {
        this.sideNavState = !this.sideNavState;

        setTimeout(() => {
            this.linkText = this.sideNavState;
        }, 200);
        this.sidenavService.sideNavState$.next(this.sideNavState);
    }
}
